import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button as AntButton,
  Modal,
  Select,
  Table,
  TableColumnsType,
} from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store/rootReducer';
import useAxios from '../../../../hooks/useAxios'
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import Button from '../../../Button';
import Checkbox from '../../../Checkbox';
import { IGetSteps } from '../../../../models/courses.model';
import {
  IGetCurrentSemester,
  IGetSemesters,
  ISemester,
} from '../../../../models/semesters.model';
import { getSteps } from '../../../../api/courses.api';
import {
  getCurrentSemester,
  getSemesters,
} from '../../../../api/semesters.api';
import { IGetAllClasses } from '../../../../models/campusClasses.model';
import { getAllSemesterClasses } from '../../../../api/campusClasses.api';
import config from '../../../../lib/config';
import { IGetStudents, IStudent } from '../../../../models/pushMessage.model';
import { getStudents } from '../../../../api/pushMessage.api';
import _ from 'lodash';
import SelectCampus from '../../../SelectCampus';
import { IGetEmployees } from '../../../../models/employees.model';
import { getEmployees } from '../../../../api/employees.api';
import { getReverseYears } from '../../../../lib/util';

interface IProps {
  visible: boolean;
  setVisible: (visible: boolean) => void;
  students: IStudent[];
  setStudents: (students: IStudent[]) => void;
}

interface ILessonWeekDayGroup {
  detailCode: string[];
  korName: string[];
}


const SelectStudentModal: FC<IProps> = ({
  visible,
  setVisible,
  students,
  setStudents,
}) => {
  const { t } = useTranslation();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  const { data: res, isLoading, error, execute } = useAxios<IGetSemesters>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetSteps>();
  const {
    data: res3,
    isLoading: isLoading3,
    error: error3,
    execute: execute3,
  } = useAxios<IGetAllClasses>();
  const {
    data: res4,
    isLoading: isLoading4,
    error: error4,
    execute: execute4,
  } = useAxios<IGetCurrentSemester>();
  const {
    data: res5,
    isLoading: isLoading5,
    error: error5,
    execute: execute5,
  } = useAxios<IGetStudents>();
  const {
    data: res6,
    isLoading: isLoading6,
    error: error6,
    execute: execute6,
  } = useAxios<IGetEmployees>();

  // SY0005 = 연도
  const SY0005 = commonCodes.find(item => item.commonCode === 'SY0005')?.detailCode;
  // PR0002 = 학기유형
  const PR0002 = commonCodes.find((item) => item.commonCode === 'PR0002')?.detailCode;

  const [semesterCode, setSemesterCode] = useState<number>();
  const [courseCode, setCourseCode] = useState<string>();
  const [courseList, setCourseList] = useState<any>([]);
  const [classCode, setClassCode] = useState<string>();
  const [classes, setClasses] = useState<any>([]);
  const [keyword, setKeyword] = useState('');
  const [data, setData] = useState<IStudent[]>([]);
  const [page, setPage] = useState(1);
  const [memberCodes, setMemberCodes] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [selectsKeys, setSelectsKeys] = useState<React.Key[]>([]);
  const [lessonWeekDay, setLessonWeekDay] = useState<string>();
  const [employeeCode, setEmployeeCode] = useState<string>();
  const [year, setYear] = useState<string>();
  const [semesterType, setSemesterType] = useState("RG");
  const [semesterData, setSemesterData] = useState<ISemester[]>([]);
  
  // SY0001 = 요일
  const SY0001 = commonCodes.find(item => item.commonCode === 'SY0001')?.detailCode;

  const LESSON_WEEK_DAY_GROUP: ILessonWeekDayGroup[] = [
    { detailCode: ['MO', 'WE', 'FR'], korName: ['월', '수', '금'] },
    { detailCode: ['TU', 'TH'], korName: ['화', '목'] }
  ]

  useEffect(() => {
    execute(getSemesters());
    execute2(getSteps());
    execute4(getCurrentSemester());

    setLessonWeekDay('');
    setEmployeeCode('');
    setStudents(students.map((item)=> ({ ...item, params: {} })));
  }, []);

  useEffect(() => {
    const _memberCodes = students.map((item) => item.memberCode);

    const viewMemberCodes = data
      .map(({ memberCode }) => memberCode);

    let _isSelectAll = true;
    for (const viewMemberCode of viewMemberCodes) {
      if (!_memberCodes.includes(viewMemberCode)) {
        _isSelectAll = false;
        break;
      }
    }
    setIsSelectAll(_isSelectAll);
    setMemberCodes(_memberCodes);
  }, [students, data, page]);

  // course = level
  useEffect(() => {
    if (res2 && !isLoading2) {
      const list: any = [];
      res2.data.forEach((item) => {
        list.push({
          courseCode: item.courseCode,
          courseName: item.courseName,
        });
      });
      setCourseList(list);
    }
  }, [res2, isLoading2]);

  useEffect(() => {
    if (res3 && !isLoading3) {
      setClasses(res3.data);
    }
  }, [res3, isLoading3]);

  useEffect(() => {
    if (courseCode) {
      const levelName = courseList.find(
        (item: any) => item.courseCode === courseCode,
      )?.courseName;
      const _classes = res3?.data?.filter(
        (item: any) => item.levelName === levelName,
      );
      setClasses(_classes);
    } else if (res3?.data) {
      setClasses(res3.data);
    }
  }, [courseCode]);

  useEffect(() => {
    if (res4 && !isLoading4) {
      setSemesterCode(res4.data?.semesterCode);
    }
  }, [res4, isLoading4]);

  useEffect(() => {
    if (currentCampus && semesterCode) {
      execute3(getAllSemesterClasses(currentCampus, semesterCode));
      doGetStudents();
    }
  }, [currentCampus, semesterCode]);

  useEffect(() => {
    if (currentCampus) {
      execute6(getEmployees({ campusCode: currentCampus, authGroup: 'T' }));
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res5 && !isLoading5) {
      let _data = _.cloneDeep(res5.data.map((v)=> {
        return {
          ...v,
          key: v.memberCode
        }
      }));

      if (courseCode) {
        _data = _data.filter((item) => item.courseCode === Number(courseCode));
      }
      if (classCode) {
        _data = _data.filter((item) => item.classCode === classCode);
      }
      if (keyword) {
        _data = _data.filter(
          (item) =>
            item.memberName?.includes(keyword) ||
            item.mobilePhone?.includes(keyword) || 
            item.teacherName?.includes(keyword)
        );
      }
      setData(_.sortBy(_data, ['memberName']));
    }
  }, [res5, isLoading5]);

  useEffect(() => {
    setCourseCode('');
  }, [semesterCode]);

  useEffect(() => {
    setClassCode('');
  }, [currentCampus, semesterCode, courseCode]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
  }, [year]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
  }, [semesterType]);

  useEffect(() => {
    fnSetSemesterData(year || '', semesterType);
  }, [res]);

  const doGetStudents = () => {
    if (!currentCampus || !semesterCode) return;
    setIsSelectAll(false);
    execute5(
      getStudents({
        campusCode: currentCampus,
        semesterCode: semesterCode,
        lessonWeekDay: lessonWeekDay,
        employeeCode: employeeCode || null
      }),
    );
  };

  const onSubmit = () => {
    const _students: IStudent[] = [...students];
    memberCodes.forEach((memberCode) => {
      const student = res5?.data.find((item: IStudent) => item.memberCode === memberCode);
      if(student) {
        student['params'] = {
          memberCode: memberCode || '',
          level: '',
          sub1: '',
          sub2: ''
        };
        _students.push({...student, level: '',
          sub1: '',
          sub2: ''});
      }
    });
    console.log(_students)
    setStudents(_.uniqBy(_students, 'memberCode'));
  };

  const columns: TableColumnsType<IStudent> = [
    {
      title: 'No.',
      align: 'center',
      render: (value, record, index) => {
        return index + 1;
      },
    },
    {
      title: t('student.student.placeholder2'),
      dataIndex: 'memberName',
      key: 'memberName',
      align: 'center',
    },
    {
      title: t('student.student.column1'),
      dataIndex: 'memberEngName',
      key: 'memberEngName',
      align: 'center',
    },
    {
      title: t('sms.send.column1'),
      key: 'mobilePhone',
      dataIndex: 'mobilePhone',
      align: 'center',
    },
    {
      title: t('student.student.column125'),
      key: 'courseName',
      dataIndex: 'courseName',
      align: 'center',
    },
    {
      title: t('student.student.column126'),
      key: 'className',
      dataIndex: 'className',
      align: 'center',
    },
    {
      title: t('sms.send.column3'),
      key: 'teacherName',
      dataIndex: 'teacherName',
      align: 'center',
    },
  ];

  const rowSelection = {
    
    onChange: (selectedRowKeys: React.Key[], selectedRows: any) => {
      setMemberCodes(selectedRows.map((row: any)=> row.memberCode));
      setSelectsKeys(selectedRowKeys);
    },
    getCheckboxProps: (record: any) => ({
      disabled: (record.approvalStatus === 'AP' || record.approvalStatus === 'RJ'),
    })
  };

  const getSelectAllName = (text: string) => `${text} (${t('common.text.all')})`;

  const fnSetSemesterData = (year : string, semesterType : string) => {
    if(res) {
      let data: ISemester[] = _.cloneDeep(res.data);

      if(year) {
        data = data.filter((item: any) => item.learningYearCode === year);
      }

      if(semesterType) {
        data = data.filter((item: any) => item.semesterType === semesterType);
      }

      if(data.length === 0) {
        setSemesterCode(undefined);
      }

      setSemesterData(data);
    }
  }

  return (
    <>
      {isLoading || isLoading2 || isLoading3 || isLoading4 || isLoading5 || isLoading6 ? (
        <Loader />
      ) : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={error3} />
      <ErrorResponse error={error4} />
      <ErrorResponse error={error5} />
      <ErrorResponse error={error6} />

      <Modal
        title={t('sms.send.button1')}
        visible={visible}
        width={1200}
        centered={true}
        bodyStyle={{display: 'fiex', flexDirection: 'column'}}
        onCancel={() => setVisible(false)}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onSubmit}
              disabled={!memberCodes.length}
            >
              {t('student.student.button4')}
            </AntButton>
            <AntButton key="cancel" onClick={() => setVisible(false)} className='btn-basic gray'>
              {t('common.button.close')}
            </AntButton>
          </div>
        ]}
      >
        <div className="flex comm-gap justify-content-center">
          <div className="w-150p">
            <SelectCampus className='w-150p'/>
          </div>
          <div className="w-100p">
            <Select
              placeholder={t('statistics.refund.placeholder1')}
              value={year}
              onChange={value => setYear(value)}
               className="w-100"
            >
              {getReverseYears(SY0005).map(item => (
                <Select.Option
                  key={item.detailCode}
                  value={item.detailCode}
                >
                  {item.korName}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div className="w-100p">
            <Select
              className="w-100p"
              value={semesterType}
              onChange={value => setSemesterType(value)}
            >
              <Select.Option value="">{t('common.text.all')}</Select.Option>
              {
                PR0002?.map(({korName: label, detailCode: value}) => {
                  return (
                    <Select.Option key={value} value={value}>
                      {label}
                    </Select.Option>
                  )
                })
              }
            </Select>
          </div>
          <div className="w-120p">
            <Select
              className="w-100"
              placeholder={t('student.student.column22')}
              value={semesterCode}
              onChange={(value) => setSemesterCode(value)}
            >
              {semesterData?.map((item: any) => {
                return (
                  <Select.Option
                    key={item.semesterCode}
                    value={item.semesterCode}
                  >
                    {item.semesterName}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
          <div className="w-100p">
            <Select
              className="w-100"
              placeholder={t('student.student.column125')}
              value={courseCode}
              onChange={(value) => setCourseCode(value)}
            >
              <Select.Option value="">{ getSelectAllName(t('student.student.column125')) }</Select.Option>
              {courseList.map((item: any) => {
                return (
                  <Select.Option key={item.courseCode} value={item.courseCode}>
                    {item.courseName}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
          <div className="w-100p">
            <Select
              className="w-100"
              placeholder={t('sms.send.column4')}
              value={lessonWeekDay}
              onChange={(value) => setLessonWeekDay(value)}
            >
              <Select.Option value="">{ getSelectAllName(t('sms.send.column4')) }</Select.Option>
              {LESSON_WEEK_DAY_GROUP?.map((item: ILessonWeekDayGroup) => (
                  <Select.Option
                    key={item.detailCode.join(",")}
                    value={item.detailCode.join(",")}
                  >
                    {item.korName.join(" ")}
                  </Select.Option>
                ))}
            </Select>
          </div>
          <div className="w-100p">
            <Select
              className="w-100"
              placeholder={t('sms.send.column3')}
              value={employeeCode}
              onChange={(value) => setEmployeeCode(value)}
            >
              <Select.Option value="">{ getSelectAllName(t('sms.send.column3')) }</Select.Option>
              {res6?.data.map((item:any)=> {
                return (
                  <Select.Option key={item.memberCode} value={item.memberCode}>
                    {item.memberName}
                  </Select.Option>
                );
              })}
              
            </Select>
          </div>
          <div className="w-100p">
            <Select
              className="w-100"
              placeholder={t('student.student.column126')}
              value={classCode}
              onChange={(value) => setClassCode(value)}
            >
              <Select.Option value="">{ getSelectAllName(t('student.student.column126')) }</Select.Option>
              {classes.map((item: any) => {
                return (
                  <Select.Option key={item.classCode} value={item.classCode}>
                    {item.className}
                  </Select.Option>
                );
              })}
            </Select>
          </div>
          <input
            type="text"
            className="form-control w-150p"
            placeholder={t('sms.send.placeholder2')}
            onChange={(e) => setKeyword(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') doGetStudents();
            }}
          />
          <div className='comm-btn__area'>
            <Button
              className="btn-basic small black"
              onClick={doGetStudents}
              text={t('common.button.search')}
            />
          </div>
        </div>

        <div className="comm-bx__between">
          <span className='comm-sch_result'>{ t('sms.send.text4') } <b>{selectsKeys.length}</b> { t('sms.send.text11') }</span>
          <span className='comm-sch_result'>{ t('sms.send.text12') } <b>{data.length}</b> { t('sms.send.text13') }</span>
        </div>
        <div className='comm-table__area mt-0' style={{maxHeight: 550, overflow:'auto'}}>
          <Table

            columns={columns}
            dataSource={data}
            pagination={false}
            rowSelection={{
              type: 'checkbox',
              ...rowSelection,
            }}
            //rowKey="memberCode"
          />
        </div>
      </Modal>
    </>
  );
};

export default SelectStudentModal;
